import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { NumericFormat } from "react-number-format";
import { apiClient } from "@/services/api/client";

interface Modality {
  id: number;
  name: string;
}

// Form schema validation
const formSchema = z.object({
  number: z.string().min(1, "Código é obrigatório"),
  description: z.string().min(1, "Descrição é obrigatória"),
  modality_id: z.number().min(1, "Modalidade é obrigatória"),
  object: z.string().min(1, "Objeto é obrigatório"),
  estimatedValue: z.string().min(1, "Valor estimado é obrigatório"),
  publicationDate: z.date({
    required_error: "Data de publicação é obrigatória",
  }),
  openingDate: z.date().optional(),
  openingTime: z.string().optional(),
  responsibleOfficer: z.string().min(1, "Responsável é obrigatório"),
  status: z
    .enum(["ABERTO", "EM_ANDAMENTO", "CONCLUIDO", "ARQUIVADO"])
    .optional(),
});

export type ProcessFormValues = {
  number?: string;
  object?: string;
  modality_id?: number;
  estimatedValue?: string;
  publicationDate?: Date;
  openingDate?: Date;
  openingTime?: string;
  status?: "ABERTO" | "EM_ANDAMENTO" | "CONCLUIDO" | "ARQUIVADO";
  description?: string;
  responsibleOfficer?: string;
};

interface ProcessFormProps {
  defaultValues?: ProcessFormValues;
  onSubmit: (data: ProcessFormValues) => Promise<boolean>;
  cancelUrl: string;
  submitButtonText: string;
  title: string;
  description: string;
}

const ProcessForm: React.FC<ProcessFormProps> = ({
  defaultValues,
  onSubmit,
  cancelUrl,
  submitButtonText,
  title,
  description,
}) => {
  const navigate = useNavigate();
  const [modalities, setModalities] = useState<Modality[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchModalities = async () => {
      try {
        const response = await apiClient.get("/modalities/");
        // Garantir que a resposta seja um array
        const modalitiesData = Array.isArray(response.data)
          ? response.data
          : response.data.results || [];
        setModalities(modalitiesData);
      } catch (error) {
        console.error("Erro ao buscar modalidades:", error);
        toast({
          title: "Erro ao carregar modalidades",
          description:
            "Não foi possível carregar as modalidades. Por favor, tente novamente.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchModalities();
  }, [toast]);

  const form = useForm<ProcessFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      number: "",
      modality_id: undefined,
      object: "",
      status: "ABERTO",
      estimatedValue: "",
      publicationDate: new Date(),
      description: "",
      responsibleOfficer: "",
    },
  });

  const handleSubmit = async (data: ProcessFormValues) => {
    try {
      // Garantir que a data de publicação é um objeto Date
      if (data.publicationDate && typeof data.publicationDate === "string") {
        data.publicationDate = new Date(data.publicationDate);
      }

      const shouldReset = await onSubmit(data);
      if (shouldReset) {
        form.reset();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Erro ao enviar formulário",
        description:
          "Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="mx-auto max-w-3xl">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Número do Processo <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: 001/2025" {...field} />
                    </FormControl>
                    <FormDescription>
                      Número de identificação do processo.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="modality_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Modalidade <span className="text-red-500">*</span>
                    </FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      defaultValue={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a modalidade" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {loading ? (
                          <SelectItem value="loading" disabled>
                            Carregando...
                          </SelectItem>
                        ) : modalities.length === 0 ? (
                          <SelectItem value="empty" disabled>
                            Nenhuma modalidade disponível
                          </SelectItem>
                        ) : (
                          modalities.map((modality) => (
                            <SelectItem
                              key={modality.id}
                              value={modality.id.toString()}
                            >
                              {modality.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Selecione a modalidade de licitação.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="object"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Objeto da Licitação <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descrição detalhada do objeto"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Descreva o que está sendo licitado.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Descrição Detalhada <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Forneça detalhes adicionais sobre o processo"
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="estimatedValue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Valor Estimado (R$){" "}
                      <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <NumericFormat
                        customInput={Input}
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="R$ "
                        decimalScale={2}
                        fixedDecimalScale
                        placeholder="R$ 0,00"
                        value={field.value}
                        onValueChange={(values) => {
                          field.onChange(values.value);
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      Valor total estimado para a contratação.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="publicationDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      Data de Publicação <span className="text-red-500">*</span>
                    </FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground",
                            )}
                          >
                            {field.value ? (
                              format(field.value, "dd/MM/yyyy", {
                                locale: ptBR,
                              })
                            ) : (
                              <span>Selecione uma data</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            if (date) {
                              field.onChange(date);
                            }
                          }}
                          disabled={(date) => date < new Date("1900-01-01")}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>
                      Data de publicação do edital.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="openingDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Data de Abertura</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground",
                            )}
                          >
                            {field.value ? (
                              format(field.value, "dd/MM/yyyy", {
                                locale: ptBR,
                              })
                            ) : (
                              <span>Selecione uma data</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            if (date) {
                              field.onChange(date);
                            }
                          }}
                          disabled={(date) => date < new Date("1900-01-01")}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>
                      Data prevista para abertura do processo.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="openingTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Horário de Abertura</FormLabel>
                    <FormControl>
                      <Input
                        type="time"
                        placeholder="00:00"
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormDescription>
                      Horário previsto para abertura do processo.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="responsibleOfficer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Responsável <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Nome do responsável" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.getValues("status") !== undefined && (
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="ABERTO">Aberto</SelectItem>
                          <SelectItem value="EM_ANDAMENTO">
                            Em Andamento
                          </SelectItem>
                          <SelectItem value="CONCLUIDO">Concluído</SelectItem>
                          <SelectItem value="ARQUIVADO">Arquivado</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Status atual do processo.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </CardContent>
          <CardFooter className="flex justify-between border-t pt-6">
            <Button
              variant="outline"
              type="button"
              onClick={() => navigate(cancelUrl)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Enviando..." : submitButtonText}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default ProcessForm;
